import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SVGAssets } from '@business/assets/vectors';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FamilyFormProps } from '@business/interfaces/familyForm/familyForm';
import {
  useAddPeopleMutation,
  useGetPeopleByIdQuery,
  useGetPeopleByNameQuery,
  useGetRelationshipTypesQuery,
  useUpdatePeopleMutation
} from '@ports/internal-services/people/peopleSlice';
import { useDispatch, useSelector } from 'react-redux';
import { setAlertDetails } from '@ports/common/AlertStateSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { familyFormSchema } from '@utils/validations/family-form';
import { AlertProps } from '@business/interfaces/alert';
import { getGenderOptions } from '@utils/constants/globals';
import { getUserData } from '@ports/common/userStateSlice';
import { getToken } from '@ports/common/sessionStateSlice';
import { uploadToS3 } from '@utils/helpers/s3Upload';
import { useUserEmailExistCheck } from '@hooks/useUserEmailExistCheck';
import { createImageFileFromUrl } from '@utils/helpers/lib/photoHelpers';
import { Input } from '../Input';
import { Button } from '../Button';
import { Select } from '../Select';
import { SearchableSelectInput } from '../SearchableInput';
import { Alert } from '../Alert';
import { CustomDatePicker } from '../CustomDatePicker';
import { UploadPhotoModal } from '../MyProfileForm';
import { ExistingPersonFound } from './ExistingPersonFound';

export type FamilyFormData = {
  name: string;
  relationshipType: string;
  nickName: string;
  gender: string;
  birthday: string;
  birthPlace: string;
  dadName: string;
  momName: string;
  spouseName: string;
  daughterName: string;
  sonName: string;
  stepdadName: string;
  dadId: string;
  momId: string;
  email: string;
};

export type FamilyMember = {
  id: string;
  name: string;
  relation: string;
};

const formId = 'familyForm';

export const FamilyForm = ({
  isOpen,
  onClose,
  name,
  peopleFiltersHandler,
  createdPersonId,
  personToMove,
  ownerId,
  setIsLoading,
  onPersonCreated
}: FamilyFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const submitBtnRef = useRef<HTMLButtonElement | null>(null);
  const userDetails = useSelector(getUserData);
  const token = useSelector(getToken);
  const [showFamilyMember, setShowFamilyMember] = useState<boolean>(false);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [currentFamilyMemberInput, setCurrentFamilyMemberInput] = useState<{
    id: string;
    name: string;
    relation: string;
  }>({ id: '', name: '', relation: '' });
  const [editingMember, setEditingMember] = useState<{
    index: number;
    id: string;
    name: string;
    relation: string;
  } | null>(null);
  const [searchText, setSearchText] = useState<string>('');
  const [isNameSelected, setIsNameSelected] = useState<boolean>(false);
  const [selectedPersonId, setSelectedPersonId] = useState<string>('');
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [alertData, setAlertData] = useState<AlertProps | null>(null);
  const [profilePictureFile, setProfilePictureFile] = useState<File | null>(
    null
  );
  const [profilePicturePreview, setProfilePicturePreview] =
    useState<string>('');
  const [hasNameInteraction, setHasNameInteraction] = useState<boolean>(false);
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const [isAllowingExistingPersonLink, setIsAllowingExistingPersonLink] =
    useState<boolean>(false);

  const [AddFamily] = useAddPeopleMutation();
  const [UpdateFamily] = useUpdatePeopleMutation();

  const {
    handleEmailExistCheck,
    emailError,
    setEmailError,
    existingUser,
    setExistingUser
  } = useUserEmailExistCheck({ formId: formId });

  const { data: peopleDataForSearch } = useGetPeopleByNameQuery(
    {
      search: searchText
    },
    { skip: !searchText }
  );
  const { data: relationshipTypeOptions } = useGetRelationshipTypesQuery({});

  const { data: peopleData, refetch: refetchPeopleData } =
    useGetPeopleByIdQuery(
      {
        id: selectedPersonId
      },
      { skip: !selectedPersonId }
    );

  useEffect(() => {
    if (selectedPersonId) {
      refetchPeopleData();
    }
  }, []);

  const relationshipOptions = useMemo(() => {
    return (
      relationshipTypeOptions?.data?.rows
        ?.filter((row: any) => row?.id !== 1 && row?.id !== 2)
        ?.map((row: any) => ({
          value: row?.id,
          label: row?.name,
          gender: row?.targetGender
        })) || []
    );
  }, [relationshipTypeOptions?.data?.rows]);

  const relationshipTypeListOptions = useMemo(() => {
    return (
      relationshipTypeOptions?.data?.rows?.map((row: any) => ({
        value: row?.id,
        label: row?.name,
        gender: row?.targetGender
      })) || []
    );
  }, [relationshipTypeOptions?.data?.rows]);

  const familySearchOptions = useMemo(() => {
    return (
      peopleDataForSearch?.data?.rows?.map((item: any) => ({
        id: item?.id,
        name: item?.name,
        profilePicUrl: item?.profileImage
      })) || []
    );
  }, [peopleDataForSearch?.data?.rows]);

  // Filter out selected person from family member options
  const filteredFamilySearchOptions = useMemo(() => {
    return familySearchOptions.filter(
      option => option?.id !== selectedPersonId
    );
  }, [familySearchOptions, selectedPersonId]);

  const shouldDisableFields = isNameSelected && !!peopleData?.data?.type;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    setFocus,
    trigger
  } = useForm<FamilyFormData>({
    resolver: yupResolver(familyFormSchema),
    defaultValues: {
      name: name || '',
      relationshipType: '',
      nickName: '',
      gender: '',
      birthday: '',
      birthPlace: '',
      dadName: '',
      momName: '',
      spouseName: '',
      daughterName: '',
      sonName: '',
      stepdadName: '',
      dadId: '',
      momId: '',
      email: ''
    },
    context: { skipConditionalRequired: shouldDisableFields }
  } as any);

  const genderValue = watch('gender');

  useEffect(() => {
    if (personToMove) {
      setValue('name', personToMove.name);
      setSearchText(personToMove.name);
      setIsNameSelected(true);
      setSelectedPersonId(personToMove.id);
    } else if (name) {
      setValue('name', name);
      setSearchText(name);
    }
  }, [name, personToMove, setValue]);

  useEffect(() => {
    const firstError = Object.keys(errors)[0] as keyof FamilyFormData;
    if (firstError) {
      setFocus(firstError);
    }
  }, [errors, setFocus]);

  useEffect(() => {
    trigger(['relationshipType', 'gender']);
  }, [shouldDisableFields, trigger]);

  function afterSubmission() {
    setIsLoading(false);
    setFamilyMembers([]);
    setCurrentFamilyMemberInput({ id: '', name: '', relation: '' });
    setShowFamilyMember(false);
    setProfilePictureFile(null);
    setProfilePicturePreview('');
    peopleFiltersHandler();
    setExistingUser(null);
    setEmailError('');
    setIsAllowingExistingPersonLink(false);
    reset();
    onClose(true);
  }

  const handleFormSubmit = async (data: FamilyFormData) => {
    if (!isAllowingExistingPersonLink) {
      const emailExists = await handleEmailExistCheck(data?.email); // Check email existence on form submit and set error if exists
      if (!emailExists) {
        return;
      }
    }

    const fatherRelation = relationshipTypeListOptions.find(
      (option: any) => option?.name?.toLowerCase() === 'father'
    );
    const motherRelation = relationshipTypeListOptions.find(
      (option: any) => option?.name?.toLowerCase() === 'mother'
    );

    setIsLoading(true);
    const combinedFamilyMembers = [
      ...(data.dadName
        ? [
            {
              id: data.dadId || '',
              name: data.dadName.trim(),
              relation: fatherRelation ? String(fatherRelation?.value) : '1', // Default to '1' for Father if not found in options
              gender: fatherRelation?.gender ?? 'male' // Default to 'male' for Father if not found in options
            }
          ]
        : []),

      ...(data.momName
        ? [
            {
              id: data.momId || '',
              name: data.momName.trim(),
              relation: motherRelation ? String(motherRelation?.value) : '2', // Default to '2' for Mother if not found in options
              gender: motherRelation?.gender ?? 'female' // Default to 'female' for Mother if not found in options
            }
          ]
        : []),

      ...familyMembers.map(member => ({
        id: member.id || '',
        name: member.name.trim(),
        relation: member.relation.trim()
      }))
    ];

    // ✅ Use personToMove ID if available
    const peopleIdToUse =
      personToMove?.id || selectedPersonId || createdPersonId || '';

    if (createdPersonId || personToMove) {
      // Update existing person
      const updateDataObj = {
        id: personToMove?.id || createdPersonId,
        updatePeople: {
          peopleId: peopleIdToUse,
          relatedPeopleId: ownerId,
          type: 'family',
          name: personToMove?.name || data.name || name,
          relationship: data.relationshipType,
          nickname: data.nickName,
          gender: data.gender,
          dateOfBirth: data.birthday || undefined,
          birthPlace: data.birthPlace,
          familyMembers: combinedFamilyMembers,
          email: data?.email === '' ? null : data?.email
        }
      };

      try {
        const res: any = await UpdateFamily(updateDataObj);

        if (res?.data) {
          // If profile picture is selected, upload it
          if (profilePictureFile) {
            try {
              const idToken = token || '';
              if (!idToken) {
                throw new Error('Authentication token not found');
              }

              // Upload to S3
              const imageUrl = await uploadToS3(
                profilePictureFile,
                idToken,
                userDetails?.userId || '',
                'profile',
                '',
                {
                  userId: userDetails?.userId
                }
              );

              // Update people with profile image
              const updateImageDataObj = {
                id: personToMove?.id || createdPersonId,
                updatePeople: {
                  profileImage: imageUrl
                }
              };

              await UpdateFamily(updateImageDataObj);
            } catch (uploadError: any) {
              console.error('Profile picture upload error:', uploadError);
              dispatch(
                setAlertDetails({
                  alert: {
                    type: 'warning',
                    message: t(
                      'Family member updated but profile picture upload failed'
                    )
                  },
                  scope: 'people'
                })
              );
            }
          }

          dispatch(
            setAlertDetails({
              alert: {
                type: 'success',
                message: 'Family member updated successfully'
              },
              scope: 'people'
            })
          );
          afterSubmission();
        } else {
          setIsLoading(false);
          setShowAlert(true);
          setAlertData({
            type: 'error',
            message: res?.error?.data?.data
          });
        }
      } catch (err: any) {
        setIsLoading(false);
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: err?.message
        });
      }
    } else {
      // Add new person
      const dataObj = {
        createPeople: {
          peopleId: peopleIdToUse,
          relatedPeopleId: ownerId,
          type: 'family',
          name: data.name || name,
          relationshipType: data.relationshipType,
          nickname: data.nickName,
          gender: data.gender,
          dateOfBirth: data.birthday || undefined,
          birthPlace: data.birthPlace,
          familyMembers: combinedFamilyMembers,
          email: data?.email === '' ? undefined : data?.email
        }
      };

      try {
        const res: any = await AddFamily(dataObj);

        if (res?.data) {
          const newPeopleId = res.data.data.id;

          onPersonCreated?.(newPeopleId);

          // If profile picture is selected, upload it
          if (profilePictureFile && newPeopleId) {
            try {
              const idToken = token || '';
              if (!idToken) {
                throw new Error('Authentication token not found');
              }

              // Upload to S3
              const imageUrl = await uploadToS3(
                profilePictureFile,
                idToken,
                userDetails?.userId || '',
                'profile',
                '',
                {
                  userId: userDetails?.userId
                }
              );

              // Update people with profile image
              const updateDataObj = {
                id: newPeopleId,
                updatePeople: {
                  profileImage: imageUrl
                }
              };

              await UpdateFamily(updateDataObj);
            } catch (uploadError: any) {
              console.error('Profile picture upload error:', uploadError);
              dispatch(
                setAlertDetails({
                  alert: {
                    type: 'warning',
                    message: t(
                      'Family member added but profile picture upload failed'
                    )
                  },
                  scope: 'people'
                })
              );
            }
          }

          dispatch(
            setAlertDetails({
              alert: {
                type: 'success',
                message: 'Family member added successfully'
              },
              scope: 'people'
            })
          );
          afterSubmission();
        } else {
          setIsLoading(false);
          setShowAlert(true);
          setAlertData({
            type: 'error',
            message: res?.error?.data?.data
          });
        }
      } catch (err: any) {
        setIsLoading(false);
        setShowAlert(true);
        setAlertData({
          type: 'error',
          message: err?.message
        });
      }
    }
  };

  const handleCancel = () => {
    reset();
    setEmailError('');
    setExistingUser(null);
    setIsAllowingExistingPersonLink(false);
    setFamilyMembers([]);
    setCurrentFamilyMemberInput({ id: '', name: '', relation: '' });
    setShowFamilyMember(false);
    setSelectedPersonId('');
    setIsNameSelected(false);
    setProfilePictureFile(null);
    setProfilePicturePreview('');
    setHasNameInteraction(false);
    if (profilePicturePreview) {
      URL.revokeObjectURL(profilePicturePreview);
    }
    onClose(false);
  };

  const handleAddFamilyMember = () => {
    if (
      !currentFamilyMemberInput.name?.trim() ||
      !currentFamilyMemberInput.relation?.trim()
    )
      return;

    setFamilyMembers(prev => [
      ...prev,
      {
        id: currentFamilyMemberInput.id || '',
        name: currentFamilyMemberInput.name.trim(),
        relation: currentFamilyMemberInput.relation.trim()
      }
    ]);

    setCurrentFamilyMemberInput({ id: '', name: '', relation: '' });
    setShowFamilyMember(false);
  };

  const handleFamilyMemberInputChange = (
    field: 'name' | 'relation',
    value: any
  ) => {
    if (field === 'name') {
      setCurrentFamilyMemberInput(prev => ({
        ...prev,
        id: value?.id || '',
        name: value?.name || ''
      }));
    } else {
      setCurrentFamilyMemberInput(prev => ({
        ...prev,
        relation: value
      }));
    }
  };

  const handleRemoveFamilyMember = (index: number) => {
    setFamilyMembers(familyMembers.filter((_, i) => i !== index));

    // If the removed member is currently being edited, close edit mode
    if (editingMember?.index === index) {
      setEditingMember(null);
    }
  };

  const handleEditFamilyMember = (index: number) => {
    const member = familyMembers[index];
    setEditingMember({
      index,
      id: member.id || '',
      name: member.name,
      relation: member.relation
    });

    // hide add input while editing
    setShowFamilyMember(false);
  };

  const handleEditChange = (field: 'name' | 'relation', value: any) => {
    if (!editingMember) return;

    if (field === 'name') {
      setEditingMember({
        ...editingMember,
        id: value?.id || '',
        name: value?.name || ''
      });
    } else {
      setEditingMember({
        ...editingMember,
        relation: value
      });
    }
  };

  const handleUpdateFamilyMember = () => {
    if (
      !editingMember ||
      !editingMember.name?.trim() ||
      !editingMember.relation?.trim()
    )
      return;

    setFamilyMembers(prev => {
      const updated = [...prev];

      updated[editingMember.index] = {
        id: editingMember.id || '',
        name: editingMember.name.trim(),
        relation: editingMember.relation.trim()
      };

      return updated;
    });

    setEditingMember(null);
  };

  const handleCancelEdit = () => {
    setEditingMember(null);
  };

  const searchFamilyMembers = (value: string) => {
    setSearchText(value);
  };

  const getRelationLabel = (value: string) => {
    const option = relationshipTypeOptions?.data?.rows.find(
      (opt: any) => opt?.id === Number(value)
    );
    return option?.name || value;
  };

  const handleLinkExistingPerson = useCallback(async () => {
    const linkDataObj = {
      ...existingUser
    };

    setIsAllowingExistingPersonLink(true);
    /* setValue('name', linkDataObj?.name || '', {
      shouldValidate: true
    }); */
    if (linkDataObj?.profileImage) {
      const file = await createImageFileFromUrl(
        linkDataObj.profileImage,
        `${linkDataObj?.name.replace(/\s/g, '_')}.jpg`
      );
      setProfilePictureFile(file);
    }
    setExistingUser(null);
    setTimeout(() => {
      submitBtnRef.current?.click();
    }, 200);
  }, [existingUser, setExistingUser, setProfilePictureFile]);

  if (!isOpen) return null;

  const hasUnsavedFamilyMember =
    showFamilyMember &&
    (currentFamilyMemberInput.name.trim() !== '' ||
      currentFamilyMemberInput.relation.trim() !== '');

  const hasUnsavedEdit = !!editingMember;

  const shouldBlockSubmit = hasUnsavedFamilyMember || hasUnsavedEdit;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6 relative animate-fadeIn">
        {/* Close Button */}
        <Button
          btnStyle="link"
          className="absolute right-4 top-4"
          onClick={handleCancel}>
          <SVGAssets.CloseIconGray className="w-4 h-4" />
        </Button>
        {showAlert && (
          <div>
            <Alert
              onClose={() => {
                setShowAlert(false);
              }}
              {...alertData}
              autoHide={true}
            />
          </div>
        )}

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800">
            {t('peopleDetail.addToFamily')}
          </h2>
          <p className="!text-xs text-gray-500 mt-1">
            {t('peopleDetail.addToFamilyDescription')}
          </p>
        </div>

        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          id={formId}
          className="space-y-4">
          {/* Name */}
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            {t('peopleDetail.memberName')}
            <span className="text-red-500 ml-1">*</span>
          </label>

          <SearchableSelectInput
            name="name"
            placeholder={t('peopleDetail.enterMemberName')}
            control={control}
            isWhiteTheme
            options={familySearchOptions}
            value={watch('name')}
            isDisabled={!!(name || personToMove)}
            onSearch={(value: string) => {
              setSearchText(value);
              if (value) {
                setHasNameInteraction(true);
              }
            }}
            onChange={({ id, name }) => {
              setValue('name', name, { shouldValidate: true });
              setIsNameSelected(!!id);
              setSelectedPersonId(id || '');
              if (name) {
                setHasNameInteraction(true);
              }
            }}
          />

          {errors.name && (
            <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
          )}

          {!selectedPersonId &&
            !isNameSelected &&
            hasNameInteraction &&
            watch('name') && (
              <>
                <div
                  className="flex w-full items-center cursor-pointer"
                  onClick={() => setShowUploadModal(true)}>
                  <SVGAssets.AddRoundIcon className="w-4 h-4" />
                  <span className="text-sm text-[#49712B] ml-2 font-semibold">
                    {t('addProfilePic')}
                  </span>
                </div>
                {profilePicturePreview && (
                  <div className="flex items-center gap-3 mt-2">
                    <img
                      src={profilePicturePreview}
                      alt="Profile preview"
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <Button
                      btnStyle="link"
                      type="button"
                      onClick={() => {
                        setProfilePictureFile(null);
                        if (profilePicturePreview) {
                          URL.revokeObjectURL(profilePicturePreview);
                        }
                        setProfilePicturePreview('');
                      }}
                      className="text-red-500 text-sm">
                      {t('remove')}
                    </Button>
                  </div>
                )}
              </>
            )}

          {/* Relationship Type */}
          <div
            className={
              shouldDisableFields ? 'opacity-50 pointer-events-none' : ''
            }>
            <label className="block text-sm font-semibold text-[#353535] mb-1">
              {t('peopleDetail.relationshipType')}
              <span className="text-red-500 ml-1">*</span>
            </label>

            <Select
              name="relationshipType"
              options={[
                { value: '', label: 'Select Relation' },
                ...relationshipTypeListOptions
              ]}
              value={watch('relationshipType')}
              onChange={e => {
                setValue('relationshipType', e.target.value, {
                  shouldValidate: true
                });
                const targetGender: string | undefined =
                  relationshipTypeListOptions.find(
                    option => String(option.value) === String(e.target.value)
                  )?.gender;
                if (targetGender) {
                  setValue('gender', targetGender, { shouldValidate: true });
                }
              }}
              selectClassName="friend-select mb-3"
              disabled={shouldDisableFields}
            />

            {errors.relationshipType && (
              <p className="text-red-500 text-xs mt-1">
                {errors.relationshipType.message}
              </p>
            )}
            <Input
              name="email"
              label={t('email')}
              placeholder={t('enterEmail')}
              control={control}
              isWhiteTheme
              type="text"
              disabled={shouldDisableFields}
              error={errors?.email?.message || emailError}
            />
          </div>

          {/* Nick Name & Gender */}
          <div
            className={`grid grid-cols-2 gap-4 ${shouldDisableFields ? 'opacity-50 pointer-events-none' : ''}`}>
            <Input
              name="nickName"
              label={t('peopleDetail.nickName')}
              placeholder={t('peopleDetail.enterNickName')}
              control={control}
              isWhiteTheme
              type="text"
              disabled={shouldDisableFields}
            />

            <div className="mt-2">
              <label className="block text-sm font-medium text-[#353535] mb-1">
                {t('peopleDetail.gender')}
                {genderValue !== 'any' && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>

              <Select
                name="gender"
                options={getGenderOptions(t)}
                value={genderValue}
                onChange={e =>
                  setValue('gender', e.target.value, { shouldValidate: true })
                }
                selectClassName="friend-select"
                disabled={shouldDisableFields}
              />

              {errors.gender && (
                <p className="text-xs text-red-500 mt-1">
                  {errors.gender.message}
                </p>
              )}
            </div>
          </div>

          {/* Birthday & Birth Place */}
          <div
            className={`grid grid-cols-2 gap-4 ${shouldDisableFields ? 'opacity-50 pointer-events-none' : ''}`}>
            <div>
              <label className="inputLabel" />
              <Controller
                name="birthday"
                control={control}
                render={({ field }) => (
                  <CustomDatePicker
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    maxDate={new Date()}
                    label={t('peopleDetail.birthday')}
                    placeholder={t('peopleDetail.birthday')}
                    disabled={shouldDisableFields}
                  />
                )}
              />
            </div>

            <Input
              name="birthPlace"
              label={t('peopleDetail.birthPlace')}
              placeholder={t('peopleDetail.enterBirthPlace')}
              control={control}
              type="text"
              isWhiteTheme
              disabled={shouldDisableFields}
            />
          </div>

          {watch('relationshipType') !== '10' &&
            watch('relationshipType') !== '11' && (
              <div className={`grid grid-cols-2 gap-4`}>
                <SearchableSelectInput
                  name="dadName"
                  label={t('peopleDetail.dadName')}
                  placeholder={t('peopleDetail.enterDadName')}
                  control={control}
                  isWhiteTheme
                  options={filteredFamilySearchOptions}
                  onSearch={searchFamilyMembers}
                  onChange={({ id, name }) => {
                    setValue('dadName', name, { shouldValidate: true });
                    setValue('dadId', id || '');
                  }}
                />

                <SearchableSelectInput
                  name="momName"
                  label={t('peopleDetail.momName')}
                  placeholder={t('peopleDetail.enterMomName')}
                  control={control}
                  isWhiteTheme
                  options={filteredFamilySearchOptions}
                  onSearch={searchFamilyMembers}
                  onChange={({ id, name }) => {
                    setValue('momName', name, { shouldValidate: true });
                    setValue('momId', id || '');
                  }}
                />
              </div>
            )}
          <div
            className="flex w-full items-center cursor-pointer"
            onClick={() => setShowFamilyMember(true)}>
            <SVGAssets.AddRoundIcon className="w-4 h-4" />
            <span className="text-sm text-[#49712B] ml-2 font-semibold">
              {t('peopleDetail.addFamilyMember')}
            </span>
          </div>

          {/* SAVED FAMILY MEMBERS */}
          <div className="space-y-3">
            {familyMembers.map((member, index) => (
              <div key={index} className="space-y-2">
                {editingMember?.index === index ? (
                  /* EDIT MODE */
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-4 items-center">
                    <SearchableSelectInput
                      label={t('peopleDetail.memberName')}
                      isWhiteTheme
                      value={editingMember.name}
                      options={filteredFamilySearchOptions}
                      onSearch={searchFamilyMembers}
                      onChange={({ id, name }) =>
                        handleEditChange('name', { id, name })
                      }
                      isSelectOnly={true}
                    />

                    <div>
                      <label className="block text-sm font-medium text-[#353535] mb-1">
                        {t('peopleDetail.relation')}
                      </label>
                      <Select
                        options={[
                          { value: '', label: 'Select Relation' },
                          ...relationshipOptions
                        ]}
                        value={editingMember.relation}
                        onChange={e =>
                          handleEditChange('relation', e.target.value)
                        }
                        selectClassName="friend-select"
                      />
                    </div>

                    <div className="flex items-center gap-1 pt-4">
                      <Button
                        btnStyle="link"
                        type="button"
                        onClick={handleUpdateFamilyMember}>
                        <SVGAssets.GreenTick className="w-4 h-4" />
                      </Button>

                      <Button
                        btnStyle="link"
                        type="button"
                        onClick={handleCancelEdit}>
                        <SVGAssets.CloseIcon className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ) : (
                  /* VIEW MODE */
                  <div className="flex items-center justify-between bg-white px-4 py-3 rounded-lg">
                    {/* Left content */}
                    <div className="flex flex-1 gap-16">
                      <div>
                        <p className="text-[11px] text-gray-400">
                          {t('peopleDetail.memberName')}
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {member?.name}
                        </p>
                      </div>

                      <div>
                        <p className="text-[11px] text-gray-400">
                          {t('peopleDetail.relation')}
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                          {getRelationLabel(member?.relation)}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-3">
                      <Button
                        btnStyle="link"
                        type="button"
                        onClick={() => handleEditFamilyMember(index)}>
                        <SVGAssets.PencilIcon className="w-4 h-4" />
                      </Button>

                      <Button
                        btnStyle="link"
                        type="button"
                        onClick={() => handleRemoveFamilyMember(index)}>
                        <SVGAssets.TrashIcon className="w-4 h-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* ADD INPUT – controlled by showFamilyMember */}
            {showFamilyMember && (
              <div className="grid grid-cols-[1fr_1fr_auto] gap-4 items-center">
                <SearchableSelectInput
                  label={t('peopleDetail.memberName')}
                  placeholder={t('fullName')}
                  isWhiteTheme
                  value={currentFamilyMemberInput.name}
                  options={filteredFamilySearchOptions}
                  onSearch={searchFamilyMembers}
                  onChange={({ id, name }) =>
                    handleFamilyMemberInputChange('name', { id, name })
                  }
                  isSelectOnly={true}
                />

                <div>
                  <label className="block text-sm font-medium text-[#353535] mb-1">
                    {t('peopleDetail.relation')}
                  </label>
                  <Select
                    options={[
                      { value: '', label: 'Select Relation' },
                      ...relationshipOptions
                    ]}
                    value={currentFamilyMemberInput.relation}
                    onChange={e =>
                      handleFamilyMemberInputChange('relation', e.target.value)
                    }
                    selectClassName="friend-select"
                  />
                </div>

                <div className="flex items-center gap-1 pt-4">
                  <Button
                    btnStyle="link"
                    type="button"
                    onClick={handleAddFamilyMember}
                    className="px-1 h-fit">
                    <SVGAssets.GreenTick className="w-4 h-4" />
                  </Button>

                  <Button
                    btnStyle="link"
                    type="button"
                    onClick={() => {
                      setShowFamilyMember(false);
                      setCurrentFamilyMemberInput({
                        id: '',
                        name: '',
                        relation: ''
                      });
                    }}
                    className="px-1 h-fit">
                    <SVGAssets.CloseIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>
          {shouldBlockSubmit && (
            <div className="text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded-md px-3 py-2">
              {t('peopleDetail.familyMemberChangesWarning')}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-center items-center gap-3 pt-4">
            <Button
              btnStyle="link"
              className="px-6 py-2 !bg-[#F9FEE9] border-[var(--na-primary-color)] text-[var(--na-primary-color)] font-medium rounded-md hover:bg-[#F9FEE9] text-sm"
              onClick={handleCancel}>
              {t('cancel')}
            </Button>
            <Button
              ref={submitBtnRef}
              btnStyle="primary"
              type="submit"
              className="px-6 rounded-lg py-2"
              isDisabled={shouldBlockSubmit}>
              {t('peopleDetail.addFamily')}
            </Button>
          </div>
        </form>
      </div>
      {showUploadModal && (
        <UploadPhotoModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          userId={userDetails?.userId || ''}
          isPeopleProfilePic={false}
          skipUpload={true}
          onFileSelect={(file: File) => {
            setProfilePictureFile(file);
            const previewUrl = URL.createObjectURL(file);
            setProfilePicturePreview(previewUrl);
            setShowUploadModal(false);
          }}
        />
      )}
      {existingUser && (
        <ExistingPersonFound
          existingUser={existingUser}
          handleLinkExistingPerson={handleLinkExistingPerson}
          handleUseDifferentEmail={() => {
            setIsAllowingExistingPersonLink(false);
            setExistingUser(null);
          }}
        />
      )}
    </div>
  );
};
